/**
 * On the SQL IDE, the `ExecutionServiceProvider` supplies `listDirectory / listDatabases /
 * listSchemas / listTables` — the four wire methods Databox uses to browse a connection. The
 * standalone Databox route has no SQL IDE, so those calls resolve to the context defaults (empty
 * promises) and the pane silently reads as "no items".
 *
 * This provider opens the same orchestration WS the MCP `ExecSession` uses (see prophecy-mcp
 * `src/domain/canvas/exec.ts`): connect to `/connect/fabric?token=<jwt>`, run `initialize`, then
 * expose only those four methods through `ExecutionServiceContextProvider`. Everything else in
 * `ExecutionContext` stays at its default noop.
 */
import {
  defaultValue as executionContextDefault,
  ExecutionServiceContextProvider
} from '@common/sqlIde/execution/hooks/useExecutionContext';
import { LSP } from '@prophecy/shared-core/lsp';
import { useEffect, useMemo, useState } from 'react';

import { getOrchestrationFabricExecutionWSEndPoint } from '../../utils/getServerUrl';
import { useOrchestrationAuth } from '../Fabric/OrchestrationFabric/OrchestrationAuth';
import { ExecutionClient } from '../sqlIde/execution/ExecutionClient';
import { useSessionGate } from '../sqlIde/execution/useSessionGate';

import type { ExecutionContext as ExecutionContextValue } from '../sqlIde/execution/types/types';
import type { ReactNode } from 'react';

export function DataboxConnectionSession({ children }: { readonly children: ReactNode }) {
  const dataplaneURL = useOrchestrationAuth((state) => state.dataplaneURL);
  const jwt = useOrchestrationAuth((state) => state.jwt);
  const [client, setClient] = useState<ExecutionClient | null>(null);

  useEffect(() => {
    if (!dataplaneURL || !jwt) return undefined;

    const url = getOrchestrationFabricExecutionWSEndPoint(dataplaneURL, { token: jwt });
    const next = new ExecutionClient(url);

    const off = next.listen(LSP.Notification.open, async () => {
      try {
        await next.initialize({});
        setClient(next);
      } catch {
        setClient(null);
      }
    });

    return () => {
      off?.();
      setClient((current) => (current === next ? null : current));
      try {
        next.detach();
      } catch {
        /* ignore */
      }
    };
  }, [dataplaneURL, jwt]);

  const awaitClient = useSessionGate(client);

  const value = useMemo<ExecutionContextValue>(
    () => ({
      ...executionContextDefault,
      // The browse calls wait for the socket rather than answering `[]` on a cold client: an empty
      // array reads as "this connection is empty" and nothing re-lists once the socket lands.
      // The client returns `DirectoryObjects[] | null`; the context contract is `[]` for empty.
      listDirectory: async (params) => (await (await awaitClient()).listDirectory(params)) ?? [],
      listDatabases: async (params) => (await awaitClient()).listDatabases(params),
      listSchemas: async (params) => (await awaitClient()).listSchemas(params),
      listTables: async (params) => (await awaitClient()).listTables(params),
      getExecutionClient: () => client ?? undefined
    }),
    [awaitClient, client]
  );

  return <ExecutionServiceContextProvider value={value}>{children}</ExecutionServiceContextProvider>;
}
